import { minorToMajorUnits } from "@lms/backend/lib/courses/price-format";
import { httpClient } from "@/services/http/client";

export type StaffCourse = {
  id: string;
  slug: string;
  title: string;
  status: string;
  courseType?: string;
  /** Distinct, non-revoked enrolments (CRS-009). */
  enrolledStudentCount?: number;
  updatedAt?: string;
};

export type InstructorSubmission = {
  submissionId: string;
  submittedAt: string;
  fileName: string;
  studentUserId: string;
  studentName: string | null;
  studentEmail: string;
  courseId: string;
  courseTitle: string;
  courseSlug: string;
  lessonId: string;
  lessonTitle: string;
  assignmentId: string;
  grade: string | null;
  feedback: string | null;
};

export type InstructorEnrollment = {
  enrollmentId: string;
  userId: string;
  studentName: string | null;
  studentEmail: string;
  status: string;
  cohortId: string;
  cohortName: string;
  enrolledAt: string;
};

export type CourseBuilderModule = {
  id: string;
  title: string;
  sortOrder: number;
  lessons: Array<{
    id: string;
    title: string;
    subtitle: string | null;
    contentType: string;
    sortOrder: number;
    videoProvider: string;
    videoStatus: string;
    hasVideo: boolean;
    /** R-3: completable / holds the course until its video is ready / doesn't count. */
    completionState?: "completable" | "awaiting_video" | "not_counted";
    hasBodyHtml: boolean;
    attachmentCount: number;
    unlockAt: string | null;
  }>;
};

export type CourseBuilderData = {
  course: {
    id: string;
    title: string;
    slug: string;
    courseType: string;
    status: string;
    metadata: Record<string, unknown> | null;
  };
  modules: CourseBuilderModule[];
  /** CRS-11: what this school has set up. */
  capabilities?: { liveClasses: boolean };
};

export type StaffCoursePrice = {
  currency: string;
  isActive: boolean;
  amount: number;
};

export type StaffStudentDetail = {
  enrollment: {
    studentName: string | null;
    studentEmail: string;
    studentPhone: string | null;
    status: string;
    cohortName: string;
    enrolledAt: string;
    lastActiveAt: string | null;
    paymentAmount: number | null;
    paymentCurrency: string | null;
    paymentStatus: string | null;
    paymentProvider: string | null;
  };
  metrics: {
    progressPercent: number;
    completedLessons: number;
    totalLessons: number;
    quizAvgPct: number | null;
    homeworkSubmitted: number;
    homeworkTotal: number;
    homeworkOnTime: number;
    homeworkLate: number;
    homeworkMissing: number;
    homeworkPending: number;
  };
  submissions: Array<{
    submissionId: string;
    lessonTitle: string;
    fileName: string;
    submittedAt: string;
    grade: string | null;
    feedback: string | null;
  }>;
};

export type ForumHubCourse = {
  id: string;
  slug: string;
  title: string;
  threadCount: number;
};

export type TenantMember = {
  userId: string;
  email: string;
  name: string | null;
  role: string;
  /** H4: suspended from Admin → Access report. */
  suspendedAt?: string | null;
};

export type TenantInvite = {
  id: string;
  token: string;
  role: string;
  inviteUrl?: string;
  maxUses: number | null;
  useCount: number;
  expiresAt?: string | null;
};

export function staffPricesToForm(prices: StaffCoursePrice[]) {
  const inr = prices.find((p) => p.currency === "INR" && p.isActive && p.amount > 0);
  const usd = prices.find((p) => p.currency === "USD" && p.isActive && p.amount > 0);
  return {
    // CRS-18: exact minor → major conversion (cents survive).
    inrRupees: inr ? minorToMajorUnits(inr.amount, "INR") : 0,
    usdDollars: usd ? minorToMajorUnits(usd.amount, "USD") : 0,
  };
}

export async function fetchStaffCourses(): Promise<StaffCourse[]> {
  const res = await httpClient.get<{ data: { courses: StaffCourse[] } }>("/v1/instructor/courses");
  return res.data.data.courses;
}

export async function fetchInstructorSubmissions(): Promise<InstructorSubmission[]> {
  const res = await httpClient.get<{ data: { submissions: InstructorSubmission[] } }>(
    "/v1/instructor/submissions",
  );
  return res.data.data.submissions;
}

export type InstructorDashboardSummary = {
  studentsEnrolled: number;
  submissionsAwaitingMarking: number;
};

export async function fetchInstructorDashboardSummary(): Promise<InstructorDashboardSummary> {
  const res = await httpClient.get<{ data: { summary: InstructorDashboardSummary } }>(
    "/v1/instructor/dashboard-summary",
  );
  return res.data.data.summary;
}

export async function fetchCourseEnrollments(courseId: string): Promise<InstructorEnrollment[]> {
  const res = await httpClient.get<{ data: { enrollments: InstructorEnrollment[] } }>(
    `/v1/instructor/courses/${courseId}/enrollments`,
  );
  return res.data.data.enrollments;
}

export async function fetchCourseBuilder(courseId: string): Promise<CourseBuilderData> {
  const res = await httpClient.get<{ data: CourseBuilderData }>(
    `/v1/instructor/courses/${courseId}`,
  );
  return res.data.data;
}

export async function fetchStaffCoursePrices(courseId: string): Promise<StaffCoursePrice[]> {
  const res = await httpClient.get<{ data: { prices: StaffCoursePrice[] } }>(
    `/v1/instructor/courses/${courseId}/prices`,
  );
  return res.data.data.prices;
}

export async function fetchStaffStudentDetail(
  courseId: string,
  userId: string,
): Promise<StaffStudentDetail> {
  const res = await httpClient.get<{ data: StaffStudentDetail }>(
    `/v1/instructor/courses/${courseId}/students/${userId}`,
  );
  return res.data.data;
}

export async function fetchForumHubCourses(): Promise<ForumHubCourse[]> {
  const res = await httpClient.get<{ data: { courses: ForumHubCourse[] } }>("/v1/forum/courses");
  return res.data.data.courses;
}

export async function fetchTenantMembers(): Promise<TenantMember[]> {
  const res = await httpClient.get<{ data: { members: TenantMember[] } }>("/v1/tenant/members");
  return res.data.data.members;
}

export async function fetchTenantInvites(tenantSlug: string): Promise<TenantInvite[]> {
  const res = await httpClient.get<{ data: { invites: TenantInvite[] } }>("/v1/tenant/invites");
  return res.data.data.invites.map((invite) => ({
    ...invite,
    inviteUrl: invite.inviteUrl ?? `/t/${tenantSlug}/invite/${encodeURIComponent(invite.token)}`,
  }));
}

/** F1: a course's teaching staff (tenant admin also gets who can be added). */
export type CourseStaffMember = {
  userId: string;
  name: string | null;
  email: string;
  role: "instructor" | "ta";
};

export type CourseStaffData = {
  staff: CourseStaffMember[];
  candidates: CourseStaffMember[];
  canManage: boolean;
};

export async function fetchCourseStaff(courseId: string): Promise<CourseStaffData> {
  const res = await httpClient.get<{ data: CourseStaffData }>(
    `/v1/instructor/courses/${courseId}/staff`,
  );
  return res.data.data;
}

export async function addCourseStaff(courseId: string, userId: string): Promise<CourseStaffMember> {
  const res = await httpClient.post<{ data: { staff: CourseStaffMember } }>(
    `/v1/instructor/courses/${courseId}/staff`,
    { userId },
  );
  return res.data.data.staff;
}

export async function removeCourseStaff(courseId: string, userId: string): Promise<void> {
  await httpClient.delete(`/v1/instructor/courses/${courseId}/staff/${userId}`);
}
